import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Logo } from "@/components/landing/Header";
import { LangFlags } from "@/components/landing/LangFlags";
import { Kicker } from "@/components/landing/Section";
import Footer from "@/components/landing/Footer";
import { COMPANY } from "@/config/links";

const linkCls = "font-bold text-moss underline decoration-moss/40 underline-offset-4 hover:decoration-moss transition-[text-decoration-color]";

// Renders {email} and {privacy} placeholders inside translated text as links.
export const RichText = ({ text }) => {
  const { t } = useLang();
  return text.split(/(\{email\}|\{privacy\})/).map((part, i) => {
    if (part === "{email}") return <a key={i} href={`mailto:${COMPANY.email}`} className={linkCls}>{COMPANY.email}</a>;
    if (part === "{privacy}") return <Link key={i} to="/privacy" className={linkCls}>{t.pages.contact.privacyLink}</Link>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

export const PageHead = ({ kicker, title, lead, children }) => (
  <div className="max-w-4xl animate-[tfj-rise_0.6s_ease-out_both]">
    <Kicker>{kicker}</Kicker>
    <h1 data-testid="page-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-ink mt-5 break-words">
      {title}
    </h1>
    {lead && <p data-testid="page-lead" className="mt-6 text-base md:text-lg text-ink-muted leading-relaxed max-w-2xl">{lead}</p>}
    {children}
  </div>
);

export default function PageShell({ testId, children }) {
  const { t } = useLang();
  return (
    <div data-testid={testId} className="paper-texture min-h-screen bg-parchment text-ink font-body flex flex-col">
      <header data-testid="page-header" className="sticky top-0 z-50 bg-parchment/90 backdrop-blur-lg border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-2 flex items-center justify-between gap-3">
          <Logo testId="page-logo" compact />
          <LangFlags />
          <Link
            to="/"
            data-testid="page-back-link"
            aria-label={t.pages.common.back}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-line bg-parchment p-2 sm:px-4 sm:py-2 text-sm font-bold text-ink hover:border-moss hover:text-moss transition-colors whitespace-nowrap"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{t.pages.common.back}</span>
          </Link>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 lg:px-12 pt-14 md:pt-20 pb-20 md:pb-28">{children}</main>
      <Footer />
    </div>
  );
}
